import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { getApiUrl } from '../config/runtime-config';

export interface Prompt {
  id?: string;
  text: string;
}

export interface Question {
  id: string;
  name: string;
  dataType: string;
  prompts: Prompt[];
}

export interface TemplateQuestions {
  templateId: string;
  templateName: string;
  questions: Question[];
}

export interface QuestionPrompt {
  questionId: string;
  questionText: string;
  prompts: Prompt[];
}

export interface SurveyPrompts {
  id: string;
  templateId: string;
  templateName: string;
  questions: QuestionPrompt[];
}

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  private readonly apiUrl = getApiUrl();
  
  // Cache for template questions data
  private cachedTemplateData: TemplateQuestions | null = null;
  private cachedTemplateId: string | null = null;
  private readonly TEMPLATE_ID_KEY = 'currentTemplateId';

  constructor(private http: HttpClient) {
    // Restore templateId from localStorage on service init
    const stored = localStorage.getItem(this.TEMPLATE_ID_KEY);
    if (stored && stored !== 'null') {
      this.cachedTemplateId = stored;
    }
  }

  /**
   * Set cached template data (called from landing page)
   */
  setCachedData(data: TemplateQuestions): void {
    this.cachedTemplateData = data;
    this.cachedTemplateId = data.templateId;
    localStorage.setItem(this.TEMPLATE_ID_KEY, data.templateId);
  }

  /**
   * Set just the templateId (persists to localStorage)
   */
  setTemplateId(templateId: string | null): void {
    this.cachedTemplateId = templateId;
    if (templateId) {
      localStorage.setItem(this.TEMPLATE_ID_KEY, templateId);
    } else {
      localStorage.removeItem(this.TEMPLATE_ID_KEY);
    }
  }

  /**
   * Get cached template data if available for the given templateId
   */
  getCachedData(templateId: string): TemplateQuestions | null {
    if (this.cachedTemplateId === templateId && this.cachedTemplateData) {
      return this.cachedTemplateData;
    }
    return null;
  }

  /**
   * Clear cached data
   */
  clearCache(): void {
    this.cachedTemplateData = null;
    this.cachedTemplateId = null;
  }

  /**
   * Get the cached template ID (for navigation back to landing)
   */
  getCachedTemplateId(): string | null {
    return this.cachedTemplateId;
  }

  /**
   * Get all photo-type questions for a template
   */
  getQuestions(templateId: string): Observable<TemplateQuestions> {
    return this.http.get<TemplateQuestions>(
      `${this.apiUrl}/api/questions?templateid=${templateId}`
    );
  }

  /**
   * Get prompts for all questions in a template
   */
  getSurveyPrompts(templateId: string): Observable<SurveyPrompts> {
    return this.http.get<SurveyPrompts>(
      `${this.apiUrl}/api/GetSurveyPrompts?templateid=${templateId}`
    );
  }

  /**
   * Get prompts for a specific question
   */
  getPromptByQuestionId(templateId: string, questionId: string): Observable<QuestionPrompt> {
    return this.http.get<QuestionPrompt>(
      `${this.apiUrl}/api/GetPromptByQuestionId?templateid=${templateId}&questionid=${questionId}`
    );
  }

}
